import { Component, Inject } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';

type ConditionType = 'emailContains';

@Component({
  selector: 'create-variant-dialog',
  templateUrl: './create-variant-dialog.component.html',
  styleUrls: ['./create-variant-dialog.component.css'],
})
export class CreateVariantDialogComponent {
  form!: FormGroup;

  nameFormControl: FormControl = new FormControl('', Validators.required);
  conditionTypeFormControl: FormControl = new FormControl(null);
  conditionValueFormControl: FormControl = new FormControl('');
  priorityFormControl: FormControl = new FormControl(0);
  activeFormControl: FormControl = new FormControl(true);

  editingConditionIndex = -1;
  conditions: Array<{ type: ConditionType; value: string }> = [];

  conditionTypeOptions = [
    { label: 'Email Address Contains', value: 'emailContains' },
  ];

  constructor(
    public matDialogRef: MatDialogRef<CreateVariantDialogComponent>,
    private fb: FormBuilder,
    @Inject(MAT_DIALOG_DATA) public data: any,
  ) {
    this.nameFormControl.setValue(this.data?.value?.name ?? '');
    this.priorityFormControl.setValue(this.data?.value?.priority ?? 0);
    this.activeFormControl.setValue(this.data?.value?.active ?? true);

    const existingConditions = this.extractConditionsFromData(this.data?.value);
    this.conditions = existingConditions;

    this.form = this.fb.group({
      name: this.nameFormControl,
      conditionType: this.conditionTypeFormControl,
      conditionValue: this.conditionValueFormControl,
      priority: this.priorityFormControl,
      active: this.activeFormControl,
    });
  }

  private normalizeEmailDomain(raw: string): string | null {
    if (!raw || typeof raw !== 'string') {
      return null;
    }

    let value = raw.trim().toLowerCase().replace(/\s+/g, '');
    if (!value) {
      return null;
    }

    if (value.includes('@') && !value.startsWith('@')) {
      const part = value.split('@').pop();
      value = part ? `@${part}` : '';
    }

    if (!value.startsWith('@')) {
      value = `@${value}`;
    }

    if (!/^@[a-z0-9.-]+\.[a-z]{2,}$/i.test(value)) {
      return null;
    }

    return value;
  }

  private normalizeEmail(raw: string): string | null {
    if (!raw || typeof raw !== 'string') {
      return null;
    }

    const value = raw.trim().toLowerCase().replace(/\s+/g, '');
    if (!value) {
      return null;
    }

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/i.test(value)) {
      return null;
    }

    return value;
  }

  private normalizeContains(raw: string): string | null {
    if (!raw || typeof raw !== 'string') {
      return null;
    }

    const value = raw.trim().toLowerCase();
    return value ? value : null;
  }

  private normalizeCondition(type: ConditionType, value: string): string | null {
    return this.normalizeContains(value);
  }

  private extractConditionsFromData(dataValue: any): Array<{ type: ConditionType; value: string }> {
    const conditions = dataValue?.conditions ?? {
      emailDomains: dataValue?.emailDomains ?? [],
      emails: dataValue?.emails ?? [],
      emailContains: dataValue?.emailContains ?? [],
    };
    const result: Array<{ type: ConditionType; value: string }> = [];

    for (const domain of conditions?.emailDomains ?? []) {
      const normalized = this.normalizeContains(domain);
      if (normalized) {
        result.push({ type: 'emailContains', value: normalized });
      }
    }

    for (const email of conditions?.emails ?? []) {
      const normalized = this.normalizeContains(email);
      if (normalized) {
        result.push({ type: 'emailContains', value: normalized });
      }
    }

    for (const containsValue of conditions?.emailContains ?? []) {
      const normalized = this.normalizeContains(containsValue);
      if (normalized) {
        result.push({ type: 'emailContains', value: normalized });
      }
    }

    return result;
  }

  getConditionTypeLabel(type: ConditionType): string {
    return this.conditionTypeOptions.find((option) => option.value === type)?.label ?? type;
  }

  addCondition(): void {
    const selectedType = this.conditionTypeFormControl.value
      ?.value as ConditionType;
    if (!selectedType) {
      this.conditionTypeFormControl.setErrors({ required: true });
      return;
    }

    const rawValue = String(this.conditionValueFormControl.value ?? '');
    const normalizedValue = this.normalizeCondition(selectedType, rawValue);

    if (!normalizedValue) {
      this.conditionValueFormControl.setErrors({ invalid: true });
      return;
    }

    const existingTypeIndex = this.conditions.findIndex(
      (condition, index) =>
        condition.type === selectedType && index !== this.editingConditionIndex,
    );

    if (existingTypeIndex >= 0) {
      this.conditionValueFormControl.setErrors({ typeExists: true });
      return;
    }

    if (this.editingConditionIndex >= 0) {
      this.conditions[this.editingConditionIndex] = {
        type: selectedType,
        value: normalizedValue,
      };
      this.editingConditionIndex = -1;
    } else {
      this.conditions.push({
        type: selectedType,
        value: normalizedValue,
      });
    }

    this.conditionValueFormControl.setValue('');
    this.conditionValueFormControl.setErrors(null);
    this.conditionTypeFormControl.setErrors(null);
  }

  editCondition(index: number): void {
    const item = this.conditions[index];
    const selectedTypeOption = this.conditionTypeOptions.find(
      (option) => option.value === item.type,
    );

    this.conditionTypeFormControl.setValue(
      selectedTypeOption ?? this.conditionTypeOptions[0],
    );
    this.editingConditionIndex = index;
    this.conditionValueFormControl.setValue(item.value);
  }

  removeCondition(index: number): void {
    this.conditions.splice(index, 1);

    if (this.editingConditionIndex === index) {
      this.editingConditionIndex = -1;
      this.conditionValueFormControl.setValue('');
      this.conditionValueFormControl.setErrors(null);
    }
  }

  get canAddCondition(): boolean {
    const selectedType = this.conditionTypeFormControl.value
      ?.value as ConditionType;
    if (!selectedType) {
      return false;
    }

    const rawValue = String(this.conditionValueFormControl.value ?? '').trim();
    const normalizedValue = this.normalizeCondition(selectedType, rawValue);
    if (!normalizedValue) {
      return false;
    }

    const existingTypeIndex = this.conditions.findIndex(
      (condition, index) =>
        condition.type === selectedType && index !== this.editingConditionIndex,
    );

    return existingTypeIndex < 0;
  }

  save(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    const name = this.nameFormControl.value?.trim();

    const pendingInput = String(this.conditionValueFormControl.value ?? '').trim();
    if (pendingInput) {
      this.addCondition();
      if (this.conditionValueFormControl.errors) {
        return;
      }
    }

    if (!name || !this.conditions.length) {
      this.conditionValueFormControl.setErrors({ invalid: true });
      return;
    }

    const conditions = {
      emailDomains: [],
      emails: [],
      emailContains: this.conditions
        .filter((condition) => condition.type === 'emailContains')
        .map((condition) => condition.value),
    };

    this.matDialogRef.close({
      changed: true,
      value: {
        name,
        conditions,
        priority: Number(this.priorityFormControl.value ?? 0),
        active: Boolean(this.activeFormControl.value),
      },
    });
  }

  deleteVariant(): void {
    this.matDialogRef.close({
      deleted: true,
    });
  }
}
